import React from 'react';
import { useTranslation } from 'react-i18next';

import { bulkUpdateFutureAiAssistantMute } from '../services/companyService';
import type {
  AiAssistantSettings,
  AssistantBulkMuteChannel,
  AssistantFutureMuteChannel,
} from '../types/Company';
import {
  ASSISTANT_FUTURE_MUTE_CHANNELS,
  resolveInboundAiEnabledForChannel,
} from '../utils/assistantMuteChannels';
import styles from './AssistantChannelMutePanels.module.css';

type FutureMuteActionKey = AssistantFutureMuteChannel | 'all';

const CHANNEL_CARD_CLASS: Record<AssistantBulkMuteChannel, string> = {
  whatsapp: styles.cardWhatsapp,
  instagram: styles.cardInstagram,
  telegram: styles.cardTelegram,
  messenger: styles.cardMessenger,
  webchat: styles.cardWebchat,
  tiktok: styles.cardTiktok,
};

interface AssistantChannelFutureMutePanelProps {
  settings: Pick<
    AiAssistantSettings,
    | 'isExternalInboundAiAssistantEnabled'
    | 'externalInboundAiAssistantChannelSettings'
  >;
  onSettingsChange: (settings: Pick<
    AiAssistantSettings,
    | 'isExternalInboundAiAssistantEnabled'
    | 'isExternalInboundAiAssistantEnabledOverridden'
    | 'externalInboundAiAssistantChannelSettings'
  >) => void;
  onNotice: (message: string) => void;
  onError: (message: string) => void;
}

export const AssistantChannelFutureMutePanel: React.FC<AssistantChannelFutureMutePanelProps> = ({
  settings,
  onSettingsChange,
  onNotice,
  onError,
}) => {
  const { t } = useTranslation('companies');
  const [actionKey, setActionKey] = React.useState<FutureMuteActionKey | null>(null);

  const globalEnabled = settings.isExternalInboundAiAssistantEnabled;

  const isInboundEnabled = React.useCallback((channel: AssistantFutureMuteChannel | null) => (
    resolveInboundAiEnabledForChannel(
      globalEnabled,
      settings.externalInboundAiAssistantChannelSettings,
      channel,
    )
  ), [globalEnabled, settings.externalInboundAiAssistantChannelSettings]);

  const runFutureUpdate = React.useCallback(async (
    nextEnabled: boolean,
    channel: AssistantFutureMuteChannel | null,
  ) => {
    const key: FutureMuteActionKey = channel ?? 'all';
    const previousSettings = {
      isExternalInboundAiAssistantEnabled: globalEnabled,
      isExternalInboundAiAssistantEnabledOverridden: globalEnabled,
      externalInboundAiAssistantChannelSettings: settings.externalInboundAiAssistantChannelSettings,
    };

    setActionKey(key);
    onError('');

    if (channel) {
      onSettingsChange({
        ...previousSettings,
        externalInboundAiAssistantChannelSettings: {
          ...settings.externalInboundAiAssistantChannelSettings,
          [channel]: nextEnabled,
        },
      });
    } else {
      onSettingsChange({
        isExternalInboundAiAssistantEnabled: nextEnabled,
        isExternalInboundAiAssistantEnabledOverridden: nextEnabled,
        externalInboundAiAssistantChannelSettings: settings.externalInboundAiAssistantChannelSettings,
      });
    }

    try {
      const result = await bulkUpdateFutureAiAssistantMute({
        muted: !nextEnabled,
        channel: channel ?? undefined,
      });

      const channelLabel = channel
        ? resolveChannelLabel(channel, t)
        : t('assistantBulkMuteAllChannels');

      if (channel) {
        onSettingsChange({
          isExternalInboundAiAssistantEnabled: globalEnabled,
          isExternalInboundAiAssistantEnabledOverridden: globalEnabled,
          externalInboundAiAssistantChannelSettings: {
            ...settings.externalInboundAiAssistantChannelSettings,
            [channel]: result.inboundAiEnabled,
          },
        });
      } else {
        onSettingsChange({
          isExternalInboundAiAssistantEnabled: result.inboundAiEnabled,
          isExternalInboundAiAssistantEnabledOverridden: result.inboundAiEnabled,
          externalInboundAiAssistantChannelSettings: settings.externalInboundAiAssistantChannelSettings,
        });
      }

      onNotice(
        t(nextEnabled ? 'assistantFutureUnmuteSuccess' : 'assistantFutureMuteSuccess', {
          channel: channelLabel,
        }),
      );
    } catch (error) {
      onSettingsChange(previousSettings);
      onError(error instanceof Error ? error.message : t('assistantFutureMuteFailed'));
    } finally {
      setActionKey(null);
    }
  }, [
    globalEnabled,
    onError,
    onNotice,
    onSettingsChange,
    settings.externalInboundAiAssistantChannelSettings,
    t,
  ]);

  const renderSwitch = (channel: AssistantFutureMuteChannel | null, channelLabel: string) => {
    const key: FutureMuteActionKey = channel ?? 'all';
    const enabled = isInboundEnabled(channel);
    const busy = actionKey === key;

    return (
      <label className={`${styles.switch} ${busy ? styles.switchBusy : ''}`}>
        <input
          type="checkbox"
          className={styles.switchInput}
          checked={enabled}
          disabled={actionKey !== null}
          aria-label={t('assistantFutureMuteSwitchAriaLabel', { channel: channelLabel })}
          onChange={event => void runFutureUpdate(event.target.checked, channel)}
        />
        <span className={styles.switchSlider} aria-hidden="true" />
      </label>
    );
  };

  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <h4 className={styles.sectionTitle}>{t('assistantFutureMuteHeading')}</h4>
        <p className={styles.sectionDescription}>{t('assistantFutureMuteDescription')}</p>
      </div>

      <div className={`${styles.card} ${styles.cardFeatured} ${styles.futureCard}`}>
        <div className={styles.cardCopy}>
          <span className={styles.channelName}>{t('assistantBulkMuteAllChannels')}</span>
          <span className={styles.channelHint}>{t('assistantFutureMuteAllChannelsHelp')}</span>
          <span className={styles.switchHint}>
            {isInboundEnabled(null)
              ? t('assistantFutureMuteSwitchOnHelp')
              : t('assistantFutureMuteSwitchOffHelp')}
          </span>
        </div>
        {renderSwitch(null, t('assistantBulkMuteAllChannels'))}
      </div>

      <div className={styles.grid}>
        {ASSISTANT_FUTURE_MUTE_CHANNELS.map(channel => {
          const channelLabel = t(channel.labelKey);
          const hasSurfaces = Boolean(channel.commentSurfaceKey);

          if (hasSurfaces && channel.commentSurfaceKey && channel.dmLabelKey && channel.commentsLabelKey) {
            const dmLabel = `${channelLabel} · ${t(channel.dmLabelKey)}`;
            const commentsLabel = `${channelLabel} · ${t(channel.commentsLabelKey)}`;

            return (
              <div
                key={channel.key}
                className={`${styles.card} ${CHANNEL_CARD_CLASS[channel.key]} ${styles.futureCardStacked}`}
              >
                <div className={styles.cardCopy}>
                  <span className={styles.channelName}>{channelLabel}</span>
                  <span className={styles.channelHint}>{t('assistantFutureMuteSplitHelp')}</span>
                </div>
                <div className={styles.surfaceRows}>
                  <div className={styles.surfaceRow}>
                    <div className={styles.cardCopy}>
                      <span className={styles.surfaceName}>{t(channel.dmLabelKey)}</span>
                      <span className={styles.switchHint}>
                        {isInboundEnabled(channel.key)
                          ? t('assistantFutureMuteSwitchOnHelpDms')
                          : t('assistantFutureMuteSwitchOffHelpDms')}
                      </span>
                    </div>
                    {renderSwitch(channel.key, dmLabel)}
                  </div>
                  <div className={styles.surfaceRow}>
                    <div className={styles.cardCopy}>
                      <span className={styles.surfaceName}>{t(channel.commentsLabelKey)}</span>
                      <span className={styles.switchHint}>
                        {isInboundEnabled(channel.commentSurfaceKey)
                          ? t('assistantFutureMuteSwitchOnHelpComments')
                          : t('assistantFutureMuteSwitchOffHelpComments')}
                      </span>
                    </div>
                    {renderSwitch(channel.commentSurfaceKey, commentsLabel)}
                  </div>
                </div>
              </div>
            );
          }

          return (
            <div
              key={channel.key}
              className={`${styles.card} ${CHANNEL_CARD_CLASS[channel.key]} ${styles.futureCard}`}
            >
              <div className={styles.cardCopy}>
                <span className={styles.channelName}>{channelLabel}</span>
                <span className={styles.switchHint}>
                  {isInboundEnabled(channel.key)
                    ? t('assistantFutureMuteSwitchOnHelp')
                    : t('assistantFutureMuteSwitchOffHelp')}
                </span>
              </div>
              {renderSwitch(channel.key, channelLabel)}
            </div>
          );
        })}
      </div>
    </section>
  );
};

function resolveChannelLabel(
  channel: AssistantFutureMuteChannel,
  t: (key: string) => string,
): string {
  if (channel === 'instagram_comments') {
    return `${t('assistantBulkMuteChannelInstagram')} · ${t('assistantFutureMuteSurfaceComments')}`;
  }

  if (channel === 'messenger_comments') {
    return `${t('assistantBulkMuteChannelMessenger')} · ${t('assistantFutureMuteSurfaceComments')}`;
  }

  const match = ASSISTANT_FUTURE_MUTE_CHANNELS.find(item => item.key === channel);
  if (!match) {
    return channel;
  }

  if (channel === 'instagram' || channel === 'messenger') {
    return `${t(match.labelKey)} · ${t('assistantFutureMuteSurfaceDms')}`;
  }

  return t(match.labelKey);
}

export default AssistantChannelFutureMutePanel;
